import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChefHat, Clock, DollarSign, TrendingUp, Zap, BarChart3, FileText, Package, Users, AlertCircle, MessageSquare, Calendar, Printer, Download, CheckCircle, Shield, Smartphone, Bell, CreditCard, Handshake, Rocket, Award } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const PageCaterers = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Traiteurs | Back-Office"; }, []);
  const [activePlan, setActivePlan] = useState('pro');

  return (
    <div className="home-container">
      <Header currentPage="caterers" lang="fr" />

      {/* Hero Caterers */}
      <section className="hero-section caterers-hero">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <ChefHat size={16} />
            <span>Back-Office Traiteurs</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-caterers)' }}>Traiteurs</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            Économisez <strong style={{ color: 'var(--actor-caterers)' }}>15h/semaine</strong> par tranche de 100 repas/jour<br />
            et augmentez vos revenus de <strong style={{ color: 'var(--actor-caterers)' }}>manière substantielle</strong>
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">15h</div>
              <div className="stat-label">Économisées/semaine<br />(100 repas/jour)</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">+</div>
              <div className="stat-label">Revenus substantiels</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">0</div>
              <div className="stat-label">Erreur de commande</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifeste — Pourquoi EcoLunch pour les Traiteurs */}
      <section className="section" data-testid="manifeste-caterers">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Pourquoi EcoLunch pour les Traiteurs ?</h2>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-fondateur">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#F97316'}}><Handshake size={30} /></div>
              <h3>Gratuit pour tous les traiteurs de moins de 20 écoles</h3>
            </div>
            <p>EcoLunch est <strong>entièrement gratuit</strong> pour tout traiteur desservant moins de 20 écoles. Aucun frais d'abonnement, aucun frais de support, aucun frais de mise à jour. Vous accédez à l'ensemble de la plateforme, incluant toutes les fonctionnalités, les mises à jour et le support technique, sans aucune condition financière.</p>
            <p>En collaborant avec nous, vous participez directement à l'optimisation du produit : vos commentaires, besoins et demandes d'amélioration contribuent à faire évoluer la plateforme en continu, afin qu'elle devienne toujours plus simple et performante pour votre réalité opérationnelle.</p>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-connect">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#22C55E'}}><Rocket size={30} /></div>
              <h3>Une plateforme, mais aussi un moteur de croissance</h3>
            </div>
            <p>EcoLunch ne donne pas seulement accès à une plateforme complète : il donne aussi accès à un <strong>levier commercial concret</strong>.</p>
            <p>Grâce à EcoLunch Connect, nous travaillons déjà avec des écoles qui souhaitent offrir un service de repas chauds, mais ne le font pas encore en raison de la complexité opérationnelle. En éliminant cette barrière à l'entrée, nous aidons non seulement les écoles à enfin offrir ce service de manière fluide et sans friction, mais nous permettons aussi aux traiteurs partenaires d'élargir leur propre réseau en même temps que nous développons le nôtre.</p>
            <p>Autrement dit, rejoindre EcoLunch, ce n'est pas seulement adopter une technologie plus moderne. C'est aussi bénéficier de l'appui d'un écosystème et d'une équipe de développement commercial qui contribuent activement à ouvrir de nouvelles portes.</p>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-fondation">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#06B6D4'}}><Award size={30} /></div>
              <h3>Une fondation sérieuse, locale et crédible</h3>
            </div>
            <p>L'approche technologique et partenariale derrière EcoLunch s'appuie sur des acteurs reconnus :</p>
            <p><strong>Sezzle Canada</strong> — paiements flexibles<br /><strong>Paysafe Montréal</strong> — traitement sécurisé<br /><strong>OVH Cloud Québec</strong> — hébergement souverain</p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section problem-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Les défis des traiteurs</h2>
            <p className="section-subtitle">Les problèmes quotidiens qui ralentissent votre activité</p>
          </div>
          <div className="problems-grid">
            <div className="problem-card">
              <div className="problem-icon"><MessageSquare size={32} style={{color: '#84CC16'}} /></div>
              <h3>Commandes chaotiques</h3>
              <p>Téléphone, email, plateforme de commande désuète (trop répétitive ou complexe, les parents lâchent avant de terminer). Les commandes arrivent de partout. Impossible de consolider efficacement.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon"><BarChart3 size={32} style={{color: '#84CC16'}} /></div>
              <h3>Excel ou système fragmenté</h3>
              <p>Multiples outils qui ne communiquent pas ensemble. Données éparpillées, doubles saisies, incohérences. Perte de temps monumentale.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon"><TrendingUp size={32} style={{color: '#84CC16'}} /></div>
              <h3>Prédiction inefficace</h3>
              <p>Sans intelligence prédictive, vos coûts d'approvisionnement peuvent dépasser vos besoins réels.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon"><Clock size={32} style={{color: '#84CC16'}} /></div>
              <h3>Zéro visibilité temps réel</h3>
              <p>Impossible de savoir où vous en êtes en temps réel. Décisions prises à l'aveugle.</p>
            </div>
          </div>
          <div className="solution-intro">
            <h3>EcoLunch élimine ces problèmes à la source</h3>
            <p>Notre back-office automatisé transforme votre cuisine en machine bien huilée. Voyez comment :</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Votre back-office intelligent</h2>
            <p className="section-subtitle">
              Chaque fonctionnalité conçue pour vous faire gagner du temps et de l'argent
            </p>
          </div>

          <div className="benefits-grid">
            {/* Gestion des commandes */}
            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Gestion des commandes automatisée</h3>
              <p>Consolidation temps réel de toutes les commandes. Zéro manipulation manuelle. Feuilles de production auto-générées PDF prêtes à imprimer.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Consolidation automatique</h3>
              <p>Toutes les commandes parents arrivent directement dans votre système. Consolidées par école, par jour, par menu.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Alertes intelligentes</h3>
              <p>Volumes anormalement élevés/bas ? Commande tardive ? Vous êtes notifié instantanément.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>API complète pour intégration</h3>
              <p>Connectez EcoLunch à tous vos systèmes internes (ERP, comptabilité, production) via nos API REST pour une synchronisation bidirectionnelle complète.</p>
            </div>

            {/* Gestion des menus */}
            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Planification des menus & stock</h3>
              <p>Créez vos menus une fois, gérez tout le reste automatiquement. Interface intuitive pour menus hebdomadaires/mensuels avec photos, descriptions, allergènes.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Package size={32} /></div>
              <h3>Calcul automatique des portions</h3>
              <p>Définissez vos recettes une fois. Le système calcule les quantités nécessaires en fonction des commandes.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Gestion des allergènes</h3>
              <p>Étiquetage automatique. Alertes pour les enfants avec restrictions alimentaires.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Package size={32} /></div>
              <h3>Suivi du stock & ingrédients</h3>
              <p>Module optionnel : suivez votre stock en temps réel, générez vos listes de courses automatiquement.</p>
            </div>

            {/* Facturation */}
            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Facturation & suivi des revenus</h3>
              <p>Comptabilité automatisée. Plus jamais de facture oubliée. Factures générées automatiquement par école ou par parent. PDF téléchargeable.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Rapports financiers détaillés</h3>
              <p>Revenus par école, par période, par menu. Exportable Excel/PDF.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><DollarSign size={32} /></div>
              <h3>Suivi des paiements</h3>
              <p>Tableau de bord des paiements en attente, reçus, en retard. Relances automatiques.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Analytics revenus</h3>
              <p>Graphiques et tendances de vos revenus. Identifiez vos menus les plus rentables.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Partenariat Sezzle Canada</h3>
              <p>En rejoignant EcoLunch, vous bénéficiez du levier commercial et du gain de conversion liés à notre partenariat exclusif avec Sezzle Canada.</p>
            </div>

            {/* Communication */}
            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Communication directe multi-acteurs</h3>
              <p>Messagerie intégrée pour parler directement aux écoles et parents. Questions, modifications, alertes.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Canal direct écoles</h3>
              <p>Communiquez instantanément avec les écoles. Questions, modifications, alertes.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Canal direct parents</h3>
              <p>Répondez aux questions des parents directement depuis votre dashboard.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Feedback temps réel</h3>
              <p>Recevez les notes et commentaires des enfants en direct. Améliorez vos menus continuellement.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Gestion des incidents</h3>
              <p>Repas oublié ? Problème qualité ? Gérez les incidents rapidement avec le système de tickets intégré.</p>
            </div>

            {/* Analytics */}
            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Analytics & optimisation</h3>
              <p>Dashboard temps réel : vue d'ensemble de votre activité, commandes du jour, revenus, satisfaction client.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>Prévisions intelligentes</h3>
              <p>IA qui prédit les volumes futurs basés sur l'historique. Optimisez vos achats.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Rapports de performance</h3>
              <p>Quels menus fonctionnent le mieux ? Quelles écoles commandent le plus ? Tout est dans les rapports.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Download size={32} /></div>
              <h3>Export données complètes</h3>
              <p>Exportez toutes vos données en CSV/Excel pour analyses avancées.</p>
            </div>
          </div>
        </div>
      </section>


      {/* Automation Message */}
      <section className="section roi-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="solution-explainer" style={{textAlign: 'center', maxWidth: '900px', margin: '0 auto'}}>
            <h2 style={{fontSize: '2.5rem', marginBottom: '1.5rem', color: 'var(--color-lime)'}}>
              TOUT EST AUTOMATISABLE
            </h2>
            <p style={{fontSize: '1.3rem', lineHeight: '1.8'}}>
              EcoLunch se synchronise avec <strong>tous vos systèmes de gestions actuels</strong>{' '}
              pour éliminer les doubles, triples entrées de données. Connectez votre ERP, 
              votre logiciel comptable, votre système de production : <strong>tout communique ensemble</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Modules Premium™ */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Modules Premium™</h2>
            <p className="section-subtitle">
              Technologies avancées propulsées par IA propriétaire
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>QPlanIQ™</h3>
              <p>Système d'auto-commande intelligente optimisé par IA propriétaire. Calcul automatique des ingrédients et achats avec IA prédictive. Prédictions de commandes basées sur l'historique et tendances. Optimisation des quantités. Suggestions d'achats intelligentes selon les saisons et disponibilité.</p>
              <div style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: 'rgba(132, 204, 22, 0.1)', borderRadius: '8px', border: '1px solid var(--actor-schools)' }}>
                <strong style={{ color: 'var(--actor-schools)' }}>À venir - Juillet 2026</strong>
              </div>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>ReportIQ™</h3>
              <p>Personnalisation et automatisation de tous les rapports. IA développée exclusivement pour la gestion de traiteur alimentaire avec algorithmes propriétaires. 100% adaptée à VOS besoins - aucune IA générique.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Package size={32} /></div>
              <h3>LogistIQ™</h3>
              <p>Gestion intelligente des livraisons propulsée par IA d'optimisation. Optimisation des routes et horaires avec algorithmes propriétaires. Planification logistique automatisée. Traçabilité complète en temps réel.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-caterers">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(249, 115, 22, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC™ <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>pour les Traiteurs</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  Une phrase suffit pour tout contrôler
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">Combien de repas demain pour toutes les écoles ?</div>
              <div className="ecopulse-mini-cmd">Revenus par école cette semaine</div>
              <div className="ecopulse-mini-cmd">Lance impression étiquettes 8h00 am demain toutes écoles</div>
              <div className="ecopulse-mini-cmd">Graphique revenus par école et par mois</div>
            </div>
            <button onClick={() => navigate('/ecopulse')} className="ecopulse-mini-link">
              Découvrir toutes les commandes EcoPulse <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>

              <div className="footer-column">
                <h4>Plateforme</h4>
                <a onClick={() => navigate('/platform')}>Architecture</a>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. Tous droits réservés.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageCaterers;
